import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApplications, useDeleteApplication, useSetApplicationStatus } from "../../api/applications";
import { useDebounce } from "../../hooks/useDebounce";
import { StatusTag } from "../../components/StatusTag";
import { PaginationBar } from "../../components/PaginationBar";
import { formatBRL } from "../../utils/money";
import { NewApplicationModal } from "./NewApplicationModal";
import { EditApplicationModal } from "./EditApplicationModal";
import { PlansModal } from "./PlansModal";
import { useToast } from "../../components/ToastProvider";
import { EditIconButton, DeleteIconButton } from "../../components/RowIconButton";
import { Application } from "../../types/dtos";

export default function ApplicationsPage() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Application | null>(null);
  const [managingPlansFor, setManagingPlansFor] = useState<number | null>(null);
  const debouncedQ = useDebounce(q);
  const { data, isLoading, isError } = useApplications({ page, pageSize: 7, q: debouncedQ || undefined });
  const setStatus = useSetApplicationStatus();
  const deleteApplication = useDeleteApplication();
  const navigate = useNavigate();
  const toast = useToast();

  async function handleToggleStatus(app: Application) {
    const nextStatus = app.status === "DESCONTINUADO" ? "ATIVO" : "DESCONTINUADO";
    try {
      await setStatus.mutateAsync({ id: app.id, status: nextStatus });
      toast(nextStatus === "ATIVO" ? "Aplicação reativada." : "Aplicação desativada.");
    } catch {
      toast("Não foi possível atualizar a situação.");
    }
  }

  async function handleDelete(app: Application) {
    if (!window.confirm(`Excluir "${app.name}" definitivamente? Essa ação não pode ser desfeita.`)) return;
    try {
      await deleteApplication.mutateAsync(app.id);
      toast("Aplicação excluída.");
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast(message ?? "Não foi possível excluir — desative em vez disso.");
    }
  }

  return (
    <div className="d-flex flex-column gap-4">
      <div className="row g-2">
        <div className="col-md-6">
          <label className="nt-field-label">Buscar</label>
          <input
            className="form-control"
            placeholder="Buscar por nome ou categoria…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-md-3" />
        <div className="col-md-3">
          <label className="nt-field-label d-none d-md-block">&nbsp;</label>
          <button type="button" className="btn btn-primary w-100" onClick={() => setShowNew(true)}>
            Nova aplicação
          </button>
        </div>
      </div>

      {isLoading && <div className="text-secondary">Carregando…</div>}
      {isError && <div className="text-danger">Não foi possível carregar as aplicações.</div>}

      {data && data.data.length === 0 && (
        <div className="text-secondary" style={{ fontSize: 13.5 }}>
          Nenhuma aplicação cadastrada ainda.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "var(--space-4)" }}>
        {data?.data.map((app) => (
          <div key={app.id} className="card elev-sm p-3 d-flex flex-column gap-3" style={{ background: "var(--color-surface)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-sm)" }}>
            <div className="d-flex align-items-start gap-3">
              <div className="flex-grow-1 min-w-0">
                <div className="nt-card-kicker">{app.category || "—"}</div>
                <div className="nt-card-title">{app.name}</div>
              </div>
              <StatusTag status={app.status} />
            </div>
            {app.description && (
              <p className="mb-0" style={{ fontSize: 13, opacity: 0.8 }}>
                {app.description}
              </p>
            )}
            <div
              className="d-grid gap-2 pt-3"
              style={{ gridTemplateColumns: "repeat(3, 1fr)", borderTop: "1px solid var(--color-divider)" }}
            >
              <div>
                <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-neutral-500)" }}>MRR</div>
                <div className="nt-tabular" style={{ fontFamily: "var(--font-heading)", fontSize: 14 }}>
                  {formatBRL(app.mrr)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-neutral-500)" }}>Assinantes</div>
                <div className="nt-tabular" style={{ fontFamily: "var(--font-heading)", fontSize: 14 }}>
                  {app.subscribers}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-neutral-500)" }}>Versão</div>
                <div className="nt-tabular" style={{ fontFamily: "var(--font-heading)", fontSize: 14 }}>
                  {app.version || "—"}
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: 12.5 }}
                onClick={() => setManagingPlansFor(app.id)}
              >
                Planos e preços
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12.5 }}
                onClick={() => navigate(`/app/clientes?q=${encodeURIComponent(app.name)}`)}
              >
                Assinantes
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12.5 }}
                disabled={setStatus.isPending}
                onClick={() => handleToggleStatus(app)}
              >
                {app.status === "DESCONTINUADO" ? "Reativar" : "Desativar"}
              </button>
              <div className="ms-auto d-flex align-items-center">
                <EditIconButton onClick={() => setEditing(app)} />
                <DeleteIconButton onClick={() => handleDelete(app)} disabled={deleteApplication.isPending} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {data && <PaginationBar page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />}

      <NewApplicationModal show={showNew} onClose={() => setShowNew(false)} />
      <EditApplicationModal application={editing} onClose={() => setEditing(null)} />
      <PlansModal applicationId={managingPlansFor} onClose={() => setManagingPlansFor(null)} />
    </div>
  );
}
